import { useState, useEffect } from "react";
import { SHEETS_URL } from "../data/constants";

// Google Apps Script round-trips are inherently slow (script cold-start +
// a redirect through script.googleusercontent.com — often several seconds).
// To avoid paying that cost on every single page visit, fetched sheets are
// cached in memory for CACHE_TTL and de-duped so concurrent callers share
// one in-flight request. See prefetchSheet() below — App.jsx calls it for
// every known sheet on mount so all of them start loading in parallel as
// soon as the site opens, instead of one at a time as each page mounts.
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const cache = {}; // sheetName -> { data, time, promise }

function fetchSheet(sheetName) {
  const entry = cache[sheetName];
  const now = Date.now();

  if (entry && entry.data && now - entry.time < CACHE_TTL) {
    return Promise.resolve(entry.data);
  }
  if (entry && entry.promise) {
    return entry.promise;
  }

  const promise = fetch(`${SHEETS_URL}?sheet=${encodeURIComponent(sheetName)}`)
    .then((res) => res.text())
    .then((text) => {
      const parsed = JSON.parse(text);
      if (!Array.isArray(parsed)) throw new Error("Unexpected response shape");
      cache[sheetName] = { data: parsed, time: Date.now() };
      return parsed;
    })
    .catch((err) => {
      delete cache[sheetName];
      throw err;
    });

  cache[sheetName] = { ...(entry || {}), promise };
  return promise;
}

// Kick off a sheet fetch without waiting on it — used to warm the cache
// before any page actually needs the data. Safe to call multiple times;
// in-flight/fresh requests are reused, not duplicated.
export function prefetchSheet(sheetName) {
  fetchSheet(sheetName).catch(() => {});
}

// Fetches a tab from the Google Sheet (via the Apps Script Web App, ?sheet=<name>).
// No fake/placeholder fallback data — callers get an honest status instead:
//   "loading" — request in flight (skipped if a fresh cached copy exists)
//   "ready"   — request succeeded (data may be an empty array if the tab has no rows)
//   "error"   — request failed or returned something unparseable
// See apps-script/Code.gs for the doGet() handler this expects.
export default function useSheetData(sheetName) {
  const cached = cache[sheetName];
  const hasFreshCache = !!(cached && cached.data && Date.now() - cached.time < CACHE_TTL);

  const [data, setData] = useState(hasFreshCache ? cached.data : []);
  const [status, setStatus] = useState(hasFreshCache ? "ready" : "loading");

  useEffect(() => {
    let cancelled = false;
    const existing = cache[sheetName];

    if (existing && existing.data && Date.now() - existing.time < CACHE_TTL) {
      setData(existing.data);
      setStatus("ready");
      return;
    }

    setStatus("loading");
    fetchSheet(sheetName)
      .then((parsed) => {
        if (!cancelled) {
          setData(parsed);
          setStatus("ready");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData([]);
          setStatus("error");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [sheetName]);

  return { data, status };
}
